(() => {
  const formatMoney = (cents, currency) => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `${(cents / 100).toFixed(2)} ${currency}`; }
  };
  let publicRatesRequest;
  const loadShopifyRates = () => {
    if (!publicRatesRequest) {
      publicRatesRequest = fetch('https://cdn.shopify.com/s/javascripts/currencies.js', {
        credentials: 'omit', signal: AbortSignal.timeout(8000)
      }).then(async response => {
        if (!response.ok) throw new Error('Shopify currency rates unavailable');
        // Read only the numeric table, without executing a remote script or
        // replacing a currency app's global. Shopify's table stores USD per unit.
        const table = (await response.text()).match(/rates:\s*\{([^}]+)\}/)?.[1];
        if (!table) throw new Error('Shopify currency table missing');
        const rates = Object.fromEntries([...table.matchAll(/([A-Z]{3}):\s*([\d.eE+-]+)/g)]
          .map(([, code, value]) => [code, Number(value)]));
        if (rates.USD !== 1) throw new Error('Unrecognized currency table');
        return rates;
      });
    }
    return publicRatesRequest;
  };
  const positiveRate = value => Number.isFinite(Number(value)) && Number(value) > 0;
  const getThresholdRate = (sourceCurrency, shopCurrency, activeCurrency, rates) => {
    if (sourceCurrency === activeCurrency) return 1;
    const nativeCurrency = window.Shopify?.currency;
    const nativeRate = nativeCurrency?.active === activeCurrency && positiveRate(nativeCurrency.rate)
      ? Number(nativeCurrency.rate) : null;
    if (sourceCurrency === shopCurrency && nativeRate !== null) return nativeRate;
    if (!positiveRate(rates?.[sourceCurrency])) return null;
    // Convert the USD offer to store currency first, then apply this market's
    // Shopify rate. Do not mistake the store-to-PKR rate for a USD-to-PKR rate.
    if (nativeRate !== null && positiveRate(rates?.[shopCurrency])) {
      return rates[sourceCurrency] / rates[shopCurrency] * nativeRate;
    }
    if (positiveRate(rates?.[activeCurrency])) return rates[sourceCurrency] / rates[activeCurrency];
    return null;
  };

  const states = new WeakMap();
  const update = (shipping, amount, currency) => {
    if (!shipping) return;
    let state = states.get(shipping);
    if (!state) { state = {}; states.set(shipping, state); }
    state.amount = amount;
    state.currency = currency;
      const current = Math.max(0, Number(amount) || 0);
      const shopCurrency = shipping.dataset.shopCurrency || currency;
      const sourceCurrency = shipping.dataset.thresholdCurrency === 'shop' ? shopCurrency
        : (shipping.dataset.thresholdCurrency || 'USD');
      const currencyRate = getThresholdRate(sourceCurrency, shopCurrency, currency, state.rates);
      shipping.dataset.presentmentCurrency = currency;
      const message = shipping.querySelector('[data-shipping-message]');
      const next = shipping.querySelector('[data-shipping-next]');
      const goalPill = next?.parentElement;
      const progress = shipping.querySelector('[data-shipping-progress]');
      const track = shipping.querySelector('[role=progressbar]');
      const steps = shipping.querySelectorAll('.tz-product__shipping-labels > span');
      const nextLabel = goalPill?.dataset.nextLabel || 'Next:';
      const allRewardsLabel = shipping.dataset.allRewardsLabel || 'All rewards unlocked';
      const shippingLabel = shipping.dataset.shippingLabel || 'shipping';
      const giftLabel = shipping.dataset.giftLabel || 'gift';
      const freeValue = shipping.querySelector('[data-shipping-free-value]');
      const giftValue = shipping.querySelector('[data-shipping-gift-value]');
      if (currencyRate === null) {
        shipping.dataset.appliedCurrencyRate = '';
        const sourceAmount = `${sourceCurrency} ${(Number(shipping.dataset.freeThreshold) / 100).toFixed(2)}`;
        message.textContent = `${shippingLabel}: ${sourceAmount}`;
        if (next) next.textContent = state.failed ? 'Local amount unavailable' : 'Converting currency…';
        if (freeValue) freeValue.textContent = sourceAmount;
        if (giftValue) giftValue.textContent = `${sourceCurrency} ${(Number(shipping.dataset.giftThreshold) / 100).toFixed(2)}`;
        progress.style.width = '0%';
        track?.removeAttribute('aria-valuenow');
        steps.forEach((step, index) => step.classList.toggle('is-active', index === 0));
        if (!state.loading && !state.failed) {
          state.loading = true;
          loadShopifyRates().then(rates => { state.rates = rates; })
            .catch(() => { state.failed = true; })
            .finally(() => { state.loading = false; if (shipping.isConnected !== false) update(shipping, state.amount, state.currency); });
        }
        return;
      }
      shipping.dataset.appliedCurrencyRate = String(currencyRate);
      const free = Math.round(Number(shipping.dataset.freeThreshold || 0) * currencyRate);
      const gift = Math.max(Math.round(Number(shipping.dataset.giftThreshold || 0) * currencyRate), free);
      if (freeValue) freeValue.textContent = formatMoney(free, currency);
      if (giftValue) giftValue.textContent = formatMoney(gift, currency);
      if (current >= gift) {
        message.textContent = shipping.dataset.giftText;
        if (next) next.textContent = allRewardsLabel;
      } else if (current >= free) {
        message.textContent = `${shipping.dataset.freeText} — add ${formatMoney(gift - current, currency)} more for a ${giftLabel}`;
        if (next) next.textContent = `${nextLabel} ${formatMoney(gift, currency)} ${giftLabel}`;
      } else {
        message.textContent = shipping.dataset.addText.replace('[amount]', formatMoney(free - current, currency));
        if (next) next.textContent = `${nextLabel} ${formatMoney(free, currency)} ${shippingLabel}`;
      }
      const progressValue = gift > 0 ? Math.min(100, current / gift * 100) : 0;
      progress.style.width = `${progressValue}%`;
      track?.setAttribute('aria-valuemax', String((gift / 100).toFixed(2)));
      track?.setAttribute('aria-valuenow', String((Math.min(current, gift) / 100).toFixed(2)));
      steps.forEach((step, index) => step.classList.toggle('is-active', index === 0 || (index === 1 && current >= free) || (index === 2 && current >= gift)));

  };
  window.TaztyShipping = { formatMoney, getThresholdRate, update };
})();
